'use client';
import { cx } from 'styled-system/css';
import { Market, SportMatchModel } from '@nf/types/odds';
import { getSportSetting } from '@/services/settings.service';
import { useGetBetTypeSetting } from '@/hooks';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { Box, IO, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { useEffect, useState } from 'react';
import { filterBetTypesFromMarkes, sortedMarketBetType } from '@/utils/odds-utils';
import { BetChoice } from '@/features/odds-page/types';
import { betTypeService } from '@/services/bettype.service';
import { TranslateType } from '@/types/bet-type-settings';
import { useTranslations } from 'next-intl';
import { useAccountInfo } from '@/hooks/query/account';
import { OddsButton } from '@/components/widgets/odds-button';
import { DropdownIcon } from '@/components/icons/svg-icons';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { Styles } from '@/features/single-match/bet-types-section/components/bet-type-header/bet-type-header.style';
import { useOddsTableMarsContext } from '@/features/odds-table-mars/context';
import { MatchOddsLoadingWrapper } from '@/features/odds-page/match-odds-loading-wrapper';
import { useParams } from 'next/navigation';

interface MatchSpecialCricketFancyContainerProps {
	match: SportMatchModel;
	markets: Market[];
	isLoading: boolean;
}

export const MatchSpecialCricketFancyContainer = ({
	match,
	markets,
	isLoading,
} : MatchSpecialCricketFancyContainerProps) => {
	const { sport } = useOddsTableMarsContext()
	const sportSetting = getSportSetting(sport);
	const [isArranging, setIsArranging] = useState<boolean>(true);
	const [betTypeMartkets, setBetTypeMartkets] = useState<Market[]>([]);
	const [betChoices, setBetChoices] = useState<Map<number, BetChoice[]>>(new Map());
	const beyTypeItems = sportSetting?.oddsPageFilterBetTypes.filter(betTypeItem => betTypeItem.group === 2) ?? [];
	beyTypeItems.push({ betTypes: [9538], group: 2 })
	const getBetTypeSetting = useGetBetTypeSetting();
	const betTypesSet: Set<number> = beyTypeItems.reduce((accumulator, currentValue) => {
		currentValue.betTypes.forEach(betType => {
			accumulator.add(betType)
		})
		return accumulator;
	}, new Set() as Set<number>);
	useEffect(() => {
		const choiceGroup = new Map<number, BetChoice[]>();
		[...betTypesSet].forEach(betType => {
			const { betChoices } = getBetTypeSetting(betType, match.GameID);
			choiceGroup.set(
				betType, 
				betChoices
			)
		})
		setBetChoices(choiceGroup)
	}, [])

	useEffect(() => {
		MaybeMatch(!isLoading)
			.chain(() => itemsEmptyCheck(markets))
			.chain(() => MaybeMatch(betChoices.size > 0))
			.fold(
				() => [],
				() => IO.of(markets)
					.map(filterBetTypesFromMarkes(betTypesSet))
					.map(sortedMarketBetType(betTypesSet))
					.map(setBetTypeMartkets)
					.map(() => isArranging && setIsArranging(false))
					.run()
			)
	}, [isLoading, markets])
	return (
		<MatchOddsLoadingWrapper isLoading={isArranging}>
			{
				betTypeMartkets.map((market, index) => {
					return (
						<MatchSpecialCricketFancy 
							market={market} 
							match={match} 
							key={index} 
						/>
					)
				})
			}
		</MatchOddsLoadingWrapper>
	)
}
interface MatchSpecialCricketFancyProps {
	market: Market;
	match: SportMatchModel;
}
const MatchSpecialCricketFancy = ({ market, match }: MatchSpecialCricketFancyProps) => {
	const betTypeId = market.BetTypeId;
	const { user } = useAccountInfo();
	const t = useTranslations('General');
	const betTypeTranslate = useTranslations('BetType');
	const oddsData = useAtomValue(oddsPageAtom);
	const getBetTypeSetting = useGetBetTypeSetting();
	const [showBetType, setShowBetType] = useState<boolean>(true);
	const { betChoices, betTypesCategory, betChoiceDisplaySetting } = getBetTypeSetting(betTypeId, match.GameID);
	const params = useParams();
	const isSingleMatchPage = params.matchId !== undefined;
	const betTypeTitle = Box(betTypeService.getBetTypeSettingTitle(betTypeId, match?.GameID ?? 0, market?.Resourceid ?? '', isSingleMatchPage))
		.map(([label, translateType]: [string, TranslateType]) => matchEquals(translateType)
			.on('General', () => t.raw(label))
			.on('BetType', () => betTypeTranslate(label))
			.otherwise(() => '')
		)
		.map((translatedTitle: string) => market ? betTypeService.getBetTypeReplaceTitle(
			translatedTitle,
			match?.GameID ?? 0,
			market
		) : translatedTitle)
		.extract();

	const betChoiceDisplay = (betTeam: string) => {
		const teamName = betTypeService.getBetChoiceDisplayTeamName(betTypeId, betTeam, match, t('Neutral_Team'), oddsData?.teamNameMap);
		return teamName ? teamName : betTypesCategory === 'fancyBackLay' ? t('lbl_draw') : null;
	}
					
	const getDisplayLine = (betTeam: string) => {
		const betLine = betChoiceDisplaySetting?.[betTeam]?.betLine;
		return betLine && market ? market[betLine] : null;
	}

	const toggleShowBetType = () => {
		setShowBetType(!showBetType);
	}

	return (
		<div className={cx('bettype', Styles.bettype)} data-open={showBetType} data-inplay="false">
			<div className="bettype__header">
				<div className="bettype__header-text">
					{betTypeTitle}
				</div>
				<div className="bettype__header-icons" onClick={toggleShowBetType}>
					<DropdownIcon />
				</div>
			</div>
			<div className="bettype__content bettype__content--cricket-back-lay">
				<div className="bettype__list">
					<div className="bettype__text"></div>
					{
						betTypesCategory === 'fancyBackLay' ? (
							<>
								<div className="bettype__title">{t('lbl_Crkt_Back')}</div>
								<div className="bettype__title">{t('lbl_Crkt_Lay')}</div>
							</>
						) : (
							<>
								<div className="bettype__title">{t('lbl_no')}</div>
								<div className="bettype__title">{t('lbl_yes')}</div>
							</>
						)
					}
				</div>
				{
					market && betChoices.map((choice, index) => {
						if (index % 2 === 1) {
							return null;
						}
						const betChoicesFilterArray = betChoices.filter((choiceItem) => choiceItem.betChoiceGroup === choice.betChoiceGroup);
						return (
							<div key={choice.betTeam} className="bettype__list">
								<div className="bettype__text">{betChoiceDisplay(choice.betTeam)}</div>
								{
									betChoicesFilterArray.map((choiceItem, choiceIndex) => {
										const line = getDisplayLine(choiceItem.betTeam);
										return (
											<OddsButton
												key={`${market.Line}_${choice.betTeam}_${choiceIndex}`}
												match={match as SportMatchModel}
												market={market}
												selection={market.Selections[choiceItem.betTeam]}
												homeTeamName={''}
												awayTeamName={''}
												variant={betTypeService.getBetChoiceDisplaySettingBetTypeClassName(market.BetTypeId, choiceItem.betTeam)}
												betTeam={market.Selections[choiceItem.betTeam].SelId}
												originalPrice={market.Selections[choiceItem.betTeam].Price}
												sport={match?.GameID}
												title={line?.toString()}
												type="OU"
												line={line?.toString()}
												oddsType={user?.OType}
												accountStatus={user?.ActStatus ?? 0}
											/>
										)
									})
								}
							</div>
						)
					})
				}
			</div>
		</div> 
	)
}
